import { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useRef } from "react";
import { DataGridHandle } from "react-data-grid";
import "react-data-grid/lib/styles.css";
import { useTranslation } from "react-i18next";
import useTableData from "../../app/hooks/useTableData";
import useWindowSize from "../../app/hooks/useWindowSize";
import { CompanyActivity } from "../../app/model/company";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { setPage } from "../../app/store/reducers/appSlice";
import i18n from "../../i18n";
import { Pagination } from "../pagination";
import ImageFormatter from "./formatters/image-formatter";
import LinkFormatter from "./formatters/link-formatter";
import { GridContainer, StyledDataGrid } from "./styles";
import { localeColumns } from "./const";

interface GridProps {
  loading?: boolean
}

export default function Grid({ loading }: GridProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { getData: getTableData } = useTableData()
  const { width } = useWindowSize()

  const ref = useRef<DataGridHandle>(null)
  const lang = i18n.language as "ru" | "en"

  const { grid: data, pagination } = useAppSelector(store => store.AppSlice)

  const columns = useMemo((): ColumnsType<any> => {
    const columns: ColumnsType<any> = [
      {
        key: localeColumns.logo[lang],
        dataIndex: localeColumns.logo[lang],
        align: "center",
        className: "logo",
        title: t("Logo"),
        width: (width <= 578 ? 90 : 150),
        render: (value, row) => <ImageFormatter value={value} isNew={row.isNewFlag} isST={row.isSTFlag} isAF={row.isAFFlag} />
      },
      {
        key: localeColumns.name[lang],
        dataIndex: localeColumns.name[lang],
        align: "center",
        title: t("CompanyName"),
        render: (value, row) => (
            <div style={{ wordBreak: 'break-normal', wordWrap: 'break-word' }}>
              <LinkFormatter link={lang === "ru" ? row.urlDetail : row.urlDetailEn} value={value} />
            </div>
        ),
      },
      //TODO Переделать следующий столбец
      { key: "activities", dataIndex: "activities", align: "center", title: t("Activities"), render: (value: CompanyActivity[]) => value.map(item => item[localeColumns.activity[lang]]).join(", ") },
      //{ key: localeColumns.country[lang], dataIndex: localeColumns.country[lang], align: "center", title: t("Country") },
      { key: localeColumns.pavilion[lang], dataIndex: localeColumns.pavilion[lang], align: "center", title: t("Pavilion"), width: 95  },
      { key: localeColumns.stend[lang], dataIndex: localeColumns.stend[lang], align: "center", title: t("StandNumber"), width: 130 },
    ]

    if (width >= 1024) {
      return columns
    }

    if (width >= 768) {
      return [columns[0], columns[1], columns[2], columns[4]]
    }

    if (width >= 578) {
      return [columns[0], columns[1], columns[2]]
    }

    return [columns[0], columns[1]]
  }, [t, width])

  const handleNavigation = (page: number) => {
    dispatch(setPage(page))
    getTableData({ page })
  }

  useEffect(() => {
    if (!ref.current) return

    const cssColumns = ref.current.element?.style.gridTemplateColumns.split(" ")
    if (!cssColumns) return

    cssColumns[3] = "auto"
    ref.current.element?.style.setProperty("grid-template-columns", cssColumns.join(" "))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current])

  return (
    <GridContainer>
      <StyledDataGrid
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={false}
        rowClassName={(row: any) => row?.vip ? "vip" : ""}
        bordered
      />
      {(pagination && pagination.pageCount > 1) && (
        <Pagination
          {...pagination!}
          onNavigate={handleNavigation}
        />
      )}
    </GridContainer>
  )
}
